<template>
    <div    class="w-full flex justify-center items-center">
        <div    class="w-full flex flex-col bg-white px-4 py-5 border-b border-gray-200 sm:px-6 justify-center">
            <div    class="flex flex-col items-center w-full">
                <!-- Header row with Meter Name & Diag info -->
                <div    class="grid grid-cols-1 md:flex md:items-center md:justify-between md:mb-2 w-full lg:w-1/2 2xl:w-1/3">


                    <div        class="">
                        <a      v-if="permissions.can('edit_meter')"
                                :href="this.company.buildCompanyRoute(`meters/${id}/edit`)"
                                class="plain-link text-2xl text-left font-semibold text-gray-900">
                            {{ name }}
                        </a>
                        <span   v-else
                                class="plain-link cursor-not-allowed text-2xl text-left font-semibold text-gray-900">
                            {{ name }}
                        </span>
                    </div>

                    <!-- Fill in some of the Diagnostic Components & Favorite Meter Here -->
                    <div    v-if="loading">
                        <div class="flex ">
                            <span :class="iconLoading"></span>
                        </div>
                    </div>
                    <div    v-else
                            class="flex flex-row shrink items-center justify-start w-1/6  lg:ml-2">
                        <div    v-if="permissions.isSonicu() && gateway900"
                                class="shrink mr-2">
                            <undecorated-link  :href="company.buildCompanyRoute(`meters/${gateway900.ID}`)">
                                <span   class="lnr icon-antenna text-gray-900"></span>
                            </undecorated-link>
                        </div>
                        <div class="shrink mr-1">
                            <battery-percent-icon v-if="batteryPercent"
                                                  v-bind="batteryPercent"></battery-percent-icon>
                        </div>
                        <div    class="shrink mx-1">
                            <signal-strength-icon v-if="signalStrength"
                                                  v-bind="signalStrength"></signal-strength-icon>
                        </div>
                        <div    class="shrink mx-1">
                          <signal-noise-ratio-icon v-if="signalNoiseRatio"
                                                   v-bind="signalNoiseRatio"></signal-noise-ratio-icon>
                        </div>
                        <div    class="mx-1">
                            <meter-favorite
                                class=""
                                :favorite="isFavorite"
                                :meter-id="id"
                            ></meter-favorite>
                        </div>
                        <div    v-if="permissions.isSonicu()"
                                class="ml-1"
                                :data-tippy-content="viewGapsTooltip">
                            <toggle     name="gaps_toggle"
                                        v-model:value="viewGaps"></toggle>
                        </div>
                    </div>
                </div>

                <!-- Date-picker / time range row -->
                <div    class="flex flex-col text-base w-full md:w-2/3 lg:w-1/2 2xl:w-1/3">
                    <!-- This example requires Tailwind CSS v2.0+ -->
                    <span class="w-full relative z-0 inline-flex grow shadow-sm rounded-md my-2">
                        <button     type="button"
                                    @click="updateStart('hour')"
                                    class="relative inline-flex grow justify-center cursor-pointer items-center px-4 py-2 rounded-l-md border border-gray-300 text-sm font-medium focus:z-10 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                                    :class="[intervalIsHour ? 'bg-blue-500 text-white hover:bg-blue-400 ' : 'bg-white text-gray-700 hover:bg-gray-200']">
                            Hour
                        </button>
                        <button     type="button"
                                    @click="updateStart('day')"
                                    class="-ml-px relative inline-flex grow justify-center cursor-pointer items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium focus:z-10 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                                    :class="[intervalIsDay ? 'bg-blue-500 text-white hover:bg-blue-400 ' : 'bg-white text-gray-700 hover:bg-gray-200']">
                            Day
                        </button>
                        <button     type="button"
                                    @click="updateStart('week')"
                                    class="-ml-px relative inline-flex grow cursor-pointer justify-center items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium focus:z-10 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                                    :class="[intervalIsWeek ? 'bg-blue-500 text-white hover:bg-blue-400 ' : 'bg-white text-gray-700 hover:bg-gray-200 ']">
                            Week
                        </button>
                        <button     type="button"
                                    @click="updateStart('month')"
                                    class="-ml-px relative inline-flex grow justify-center cursor-pointer items-center px-4 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium focus:z-10 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                                    :class="[intervalIsMonth ? 'bg-blue-500 text-white hover:bg-blue-400 ' : 'bg-white text-gray-700 hover:bg-gray-200']">
                            Month
                        </button>
                    </span>

                    <div    class="grid grid-cols-1 md:flex md:justify-between md:items-center my-2">
                        <div class="flex items-center text-base">
                            <label for="startDate" class="mr-2 w-1/5">Start</label>
                            <date-time-picker   id="startDate"
                                                class="grow"
                                                v-model="startDate"
                                                :min-date="earliestDate"
                                                :side-by-side="true"
                                                :timezone="timezone"
                                                :format="format"></date-time-picker>
                        </div>

                        <div    v-if="startIsAtMinimum"
                                class="hidden lg:flex text-yellow-700">
                            Currently at earliest date.
                        </div>

                        <div class="flex items-center text-base">
                            <label for="endDate" class="mr-2 w-1/5">End</label>
                            <date-time-picker   id="endDate"
                                                class="grow"
                                                v-model="endDate"
                                                :min-date="startDate"
                                                :side-by-side="true"
                                                :timezone="timezone"
                                                :format="format"></date-time-picker>
                        </div>

                    </div>

                    <!-- Show point pair or just this point. -->
                    <span   v-if="permissions.isSonicu() && !environment.isProduction()"
                            class="w-full relative z-0 inline-flex grow shadow-sm rounded-md my-2">
                        <button     type="button"
                                    @click="setShowSharedPoint(false)"
                                    class="relative inline-flex grow justify-center cursor-pointer items-center px-4 py-2 rounded-l-md border border-gray-300 text-sm font-medium focus:z-10 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                                    :class="[!showPairedPoint ? 'bg-blue-500 text-white hover:bg-blue-400 ' : 'bg-white text-gray-700 hover:bg-gray-200']">
                            Only this Point
                        </button>
                        <button     type="button"
                                    @click="setShowSharedPoint(true)"
                                    class="-ml-px relative inline-flex grow justify-center cursor-pointer items-center px-4 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium focus:z-10 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                                    :class="[showPairedPoint ? 'bg-blue-500 text-white hover:bg-blue-400 ' : 'bg-white text-gray-700 hover:bg-gray-200']">
                            Show Additional Linked Point
                        </button>
                    </span>
                </div>
            </div>

            <!-- Meter Graph -->
            <div    class="">
                <meter-graph-full-legacy :id="id"
                                  :name="this.name"
                                  :start-date="startDate"
                                  :end-date="endDate"
                                  :use-short-gaps="viewGaps"
                                  :file-name="graphFileName"
                                  v-model:timezone="timezone"
                                  :filter-data-ready="!loading && firstLoad"
                                  :pair-points="pairMeters"
                                  :pair-point-options="pairMeterOptions"
                                  :pair-point-id="pairPointId"
                                  :show-paired-point="showPairedPoint"
                ></meter-graph-full-legacy>
            </div>
        </div>
    </div>

</template>

<script>
import {SonicuCoreMixin} from "@/mixins/SonicuCoreMixin";
import {iconNamesMixin} from "@/mixins/IconNamesMixin"

import axios from "axios";
import tippy from 'tippy.js';
import { isNil } from "lodash"

import MeterGraphFullLegacy from "@/components/MeterGraphFullLegacy";
import loading from "@/components/elements/loading"
import Toggle from "@/components/elements/toggle"
import SignalStrengthIcon from "@/components/SignalStrengthIcon";
import BatteryPercentIcon from "@/components/BatteryPercentIcon";
import MeterFavorite from "@/components/meterFavorite";
import DateTimePicker from "@/components/Reports/DateTimePicker";
import UndecoratedLink from "@/components/EnterpriseDashboard/undecorated-link";
import SignalNoiseRatioIcon from "@/components/SignalNoiseRatioIcon";

const LOAD_DATA_DELAY = 1000

export default {
    name: "MeterViewLegacy",
    mixins: [SonicuCoreMixin, iconNamesMixin],
    components: {
        SignalNoiseRatioIcon,
        UndecoratedLink,
        MeterFavorite, BatteryPercentIcon, SignalStrengthIcon, MeterGraphFullLegacy, loading, Toggle, DateTimePicker },
    props: {
        id: Number,
        name: String,
        deviceType: String,
        start: {
            type: String,
            default: null,
        },
        end: {
            type: String,
            default: null,
        },
    },
    data() {
        return {
            _loadFunction: null,
            tippyInstance: null,
            loading: false,
            gateway900: null,
            signalStrength: null,
            batteryPercent: null,
          signalNoiseRatio: null,
            isFavorite: false,
            earliestDate: null,
            minDate: null,
            startDate: null,
            endDate: null,
            format: 'MM/DD/YYYY HH:mm',
            timezone: 'UTC',
            firstLoad: false,

            viewGaps: false,
            viewGapsTooltip: 'Show gaps on graph when there has not been data for 30 minutes.',
            pairMeters: {},
            pairMeterOptions: {},
            pairPointId: {},
            showPairedPoint: false,
        }
    },
    created() {
        this._loadFunction = _.debounce(() => {
            this.loading = true
            axios.get(this.company.buildCompanyApiInternalRoute('view-meter') + '/' + this.id).then(
                response => {
                    let data = response.data
                    this.gateway900 = data.gateway900
                    this.signalStrength = data.signalStrength
                    this.batteryPercent = data.batteryPercent
                    this.signalNoiseRatio = data.signalNoiseRatio
                    this.isFavorite = data.isFavorite
                    this.earliestDate = data.minDate
                    this.pairMeterOptions = data.pairMeterOptions
                    this.pairMeters = data.pairMetersList
                    this.pairPointId = _.isNil(data.pairMeter) ? null : data.pairMeter.ID

                    try {
                        this.minDate = new Date(data.minDate)
                    }
                    catch (e) {
                        console.warn("MeterView - Error Creating minDate from: " + data.minDate)
                        // If there is an error setting the minDate force the min date to be 1 day ago.
                        // The logic is hoping these are mostly new meters.  Maybe we just make it a long time ago.
                        let now = new Date(Date.now())
                        now.setDate(now.getDate() - 1)
                        this.minDate = now
                    }

                    this.minDate.setSeconds(0)
                    this.minDate.setMilliseconds(0)

                    this.timezone = data.timezone
                    this.loading = false
                    this.firstLoad = true
                }
            )
        }, LOAD_DATA_DELAY)
        this.startDate = new Date()
        this.endDate = new Date()
        let intervalEstablished = false
        if (!isNil(this.start) && this.start !== '') {
            this.startDate = new Date(this.start)
            intervalEstablished = true
        }
        if (!isNil(this.end) && this.end !== '') {
            this.endDate = new Date(this.end)
            intervalEstablished = true
        }

        if (!intervalEstablished) {
            this.updateStart(this.deviceType === 'sound' ? 'hour' : 'day')
        }
    },
    mounted() {
        this.loadData()
        this.tippyInstance = tippy('[data-tippy-content]');
    },
    methods: {
        loadData() {
            this._loadFunction()
        },
        updateStart(interval) {
            let timesUpdated = false
            let startDate = new Date(Date.now())
            let endDate = new Date(Date.now())

            if (interval === 'hour') {
                let newHours = startDate.getHours() - 1
                timesUpdated = true
                startDate.setHours(newHours)
            }
            else if (interval === 'day') {
                let newDate = startDate.getDate() - 1
                timesUpdated = true
                startDate.setDate(newDate)
            }
            else if (interval === 'week') {
                let newDate = startDate.getDate() - 7
                timesUpdated = true
                startDate.setDate(newDate)
            }
            else if (interval === 'month') {
                let newMonth = startDate.getMonth() - 1
                timesUpdated = true
                startDate.setMonth(newMonth)
            }

            if (timesUpdated) {
                if (!_.isNil(this.minDate)) {
                    this.startDate = new Date(Math.max(startDate.getTime(), this.minDate.getTime()))
                }
                else {
                    this.startDate = startDate
                }
                this.endDate = endDate
            }
        },
        setShowSharedPoint(shouldShow) {
            console.log(`showPairedPoint: ${this.showPairedPoint}  |  shouldShow: ${shouldShow}`)
            this.showPairedPoint = shouldShow
        },
    },
    watch: {

    },
    computed: {
        showBatteryPercent() {
            return !isNil(this.batteryPercent)
        },
        showSignalStrength() {
            return !isNil(this.signalStrength)
        },
        showSignalNoiseRatio() {
            return !isNil(this.signalNoiseRatio)
        },
        intervalIsHour() {
            let tmp = new Date(this.startDate.getTime())
            tmp.setHours(this.startDate.getHours() + 1)
            return tmp.getTime() === this.endDate.getTime()
        },
        intervalIsDay() {
            let tmp = new Date(this.startDate.getTime())
            tmp.setDate(this.startDate.getDate() + 1)
            return tmp.getTime() === this.endDate.getTime()
        },
        intervalIsWeek() {
            let tmp = new Date(this.startDate.getTime())
            tmp.setDate(this.startDate.getDate() + 7)
            return tmp.getTime() === this.endDate.getTime()
        },
        intervalIsMonth() {
            let tmp = new Date(this.startDate.getTime())
            tmp.setMonth(this.startDate.getMonth() + 1)
            return tmp.getTime() === this.endDate.getTime()
        },
        graphFileName() {
            return `${this.name} (${this.formatStart})-(${this.formatEnd})`
        },
        formatStart() {
            return `${this.startDate.getMonth() + 1}_${this.startDate.getDate()}_${this.startDate.getFullYear()} ` +
                `${this.startDate.getHours()} ${this.startDate.getMinutes()} ${this.startDate.getSeconds()}`
        },
        formatEnd() {
            return `${this.endDate.getMonth() + 1}_${this.endDate.getDate()}_${this.endDate.getFullYear()} ` +
                `${this.endDate.getHours()} ${this.endDate.getMinutes()} ${this.endDate.getSeconds()}`
        },
        startIsAtMinimum() {
            if (_.isNil(this.minDate)) {
                return false
            }
            return Math.round(this.startDate.getTime() / 1000 / 60) === Math.round(this.minDate.getTime() / 1000 / 60)
        }
    },
    beforeUnmount() {
        this._loadFunction = null
    }
}

</script>

<style scoped>

</style>